import { useMutation, useQueryClient } from '@tanstack/react-query';
import { discardDraft, publishDraft, saveDraft, startDraft } from '../services/documentTemplates';
import type { SaveDraftInput } from '../types/documentTemplates.types';
import { documentTemplateKeys } from './documentTemplateKeys';

const useInvalidateTemplate = (templateId: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: documentTemplateKeys.detail(templateId) });
    queryClient.invalidateQueries({ queryKey: documentTemplateKeys.versions(templateId) });
    queryClient.invalidateQueries({ queryKey: documentTemplateKeys.lists() });
  };
};

export const useStartDraftMutation = (templateId: string) => {
  const invalidate = useInvalidateTemplate(templateId);
  return useMutation({
    mutationFn: (fromVersionId: string | void) =>
      fromVersionId ? startDraft(templateId, fromVersionId) : startDraft(templateId),
    onSuccess: invalidate,
  });
};

export const useSaveDraftMutation = (templateId: string) => {
  const invalidate = useInvalidateTemplate(templateId);
  return useMutation({
    mutationFn: (input: SaveDraftInput) => saveDraft(templateId, input),
    onSuccess: invalidate,
  });
};

export const usePublishDraftMutation = (templateId: string) => {
  const invalidate = useInvalidateTemplate(templateId);
  return useMutation({
    mutationFn: (revision: number) => publishDraft(templateId, revision),
    onSuccess: invalidate,
  });
};

export const useDiscardDraftMutation = (templateId: string) => {
  const invalidate = useInvalidateTemplate(templateId);
  return useMutation({ mutationFn: () => discardDraft(templateId), onSuccess: invalidate });
};
